import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Firebase Imports
import { signOut } from "firebase/auth";
import { auth } from "./firebase.js";
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ReportIcon from '@mui/icons-material/Report';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import RememberMeIcon from '@mui/icons-material/RememberMe';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HomeIcon from '@mui/icons-material/Home';


// Material-UI Imports
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  ListItemButton,
} from "@mui/material";

// Icon Imports
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings as AdminPanelSettingsIcon,
  People as PeopleIcon,
  Build as BuildIcon,
  ReportProblem as ReportProblemIcon,
  Schedule as ScheduleIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Logout as LogoutIcon,
  CreditCard as CreditCardIcon,
  EventNote as EventNoteIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Checklist as ChecklistIcon,
  Domain as DomainIcon,
} from "@mui/icons-material";



function MainNavbar({ userData, userRole, isAmcRoute }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [openSections, setOpenSections] = useState({
    userManagement: false,
    warranty: false,
    amc: false,
    contractManagement: false,
    membershipManagement : false,

  });

  const navigate = useNavigate();
  const location = useLocation();
  const isRegisterPage = location.pathname === "/newusersignin";
  const isFacilityOperatorRole = ["seller", "facility_operator"].includes(
    String(userRole || "").trim().toLowerCase()
  );
  const isTechnicianRole =
    String(userRole || "").trim().toLowerCase() === "technician";

  const getDisplayRole = (role) => {
    if (["organisation_admin", "OU Admin"].includes(role)) {
      return "organisation admin";
    }
    if (["Admin", "admin"].includes(role)) {
      return "Admin";
    }
    if (["organisation_operator", "OU User", "OU Operator", "Operator", "User", "user"].includes(role)) {
      return "organisation operator";
    }
    if (["seller", "facility_operator"].includes(String(role || "").trim().toLowerCase())) {
      return "facility operator";
    }
    if (String(role || "").trim().toLowerCase() === "technician") {
      return "technician";
    }
    return role || "User";
  };

  // ── CRITICAL logout order ─────────────────────────────────────────────────
  // sessionStorage.clear() MUST come BEFORE signOut(auth).
  //
  // Reason: signOut triggers onAuthStateChanged with user=null.
  // If otpLoggedIn is still in sessionStorage at that point, the auth
  // listener in App.js will see it and restore the OTP session — preventing
  // logout from working for OTP users.
  //
  // By clearing sessionStorage first, the listener finds no otpLoggedIn flag
  // and correctly sets isLoggedIn = false.
  const handleLogout = async () => {
    try {
      // Step 1: Clear sessionStorage FIRST (removes otpLoggedIn flag)
      sessionStorage.clear();

      // Step 2: Force immediate logout by reloading the page
      // This ensures the auth state is reset before navigation
      window.location.href = "/login";
      
      // Step 3: Sign out from Firebase (will happen in background)
      await signOut(auth);
      
    } catch (err) {
      console.error("Logout error:", err);
      // Even on error, clear storage and redirect
      sessionStorage.clear();
      window.location.href = "/login";
    }
    setAnchorEl(null);
    setDrawerOpen(false);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleDrawerNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleSectionToggle = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // --- Drawer Menu Sections ---

  const renderSuperAdminLinks = () => (
    <ListItemButton onClick={() => handleDrawerNavigation("/superadmin")}>
      <ListItemIcon>
        <AdminPanelSettingsIcon />
      </ListItemIcon>
      <ListItemText primary="Tenant Management" />
    </ListItemButton>
  );

  const renderAdminLinks = () => (
    <>
      <ListItemButton onClick={() => handleSectionToggle("userManagement")}>
        <ListItemIcon>
          <PeopleIcon />
        </ListItemIcon>
        <ListItemText primary="User Management" />
        {openSections.userManagement ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={openSections.userManagement} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/user-management")}
          >
             <ListItemIcon>
              <ManageAccountsIcon />
              </ListItemIcon>
             <ListItemText primary="Manage Users" />
          </ListItemButton>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/manage-facility")}
          >
            <ListItemIcon>
              <DomainIcon />
            </ListItemIcon>
            <ListItemText primary="Manage Facility" />
          </ListItemButton>
        </List>
          <Divider sx={{ my: 1, borderBottomWidth: 2  }} />
      </Collapse>
    </>
  );

  const renderWarrantyLinks = () => (
    <>
      <ListItemButton onClick={() => handleSectionToggle("warranty")}>
        <ListItemIcon>
          <BuildIcon />
        </ListItemIcon>
        <ListItemText primary="Warranty Management" />
        {openSections.warranty ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={openSections.warranty} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/warranty_list")}
          >
            <ListItemIcon>
              <CreditCardIcon />
            </ListItemIcon>
            <ListItemText primary="Warranty Cards" />
          </ListItemButton>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/complaints_list")}
          >
            <ListItemIcon>
              <ReportProblemIcon />
            </ListItemIcon>
            <ListItemText primary="Complaints" />
          </ListItemButton>
        </List>
          <Divider sx={{ my: 1, borderBottomWidth: 2  }} />
      </Collapse>
    </>
  );

  const renderAmcLinks = () => (
    <>
      <ListItemButton onClick={() => handleSectionToggle("amc")}>
        <ListItemIcon>
          <ScheduleIcon />
        </ListItemIcon>
        <ListItemText primary="AMC Management" />
        {openSections.amc ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={openSections.amc} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/amc/schedule/list")}
          >
            <ListItemIcon>
              <EventNoteIcon />
            </ListItemIcon>
            <ListItemText primary="Maintenance Schedules" />
          </ListItemButton>
          {!isTechnicianRole && (
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() => handleDrawerNavigation("/amc/schedule/new")}
            >
              <ListItemIcon>
                <AddCircleOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="New Schedule" />
            </ListItemButton>
          )}
          {userRole === "admin" && (
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() => handleDrawerNavigation("/amc/checklist/admin")}
            >
              <ListItemIcon>
                <AssignmentTurnedInIcon />
              </ListItemIcon>
              <ListItemText primary="Admin Checklist" />
            </ListItemButton>
          )}
          {userRole === "admin" && (
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() =>
                handleDrawerNavigation("/amc/checklist/admin/advanced")
              }
            >
              <ListItemIcon>
                <AssignmentTurnedInIcon />
              </ListItemIcon>
              <ListItemText primary="Admin Checklist (Advanced)" />
            </ListItemButton>
          )}
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/amc/checklist/user")}
          >
            <ListItemIcon>
              <ChecklistIcon />
            </ListItemIcon>
            <ListItemText primary="User Checklist" />
          </ListItemButton>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() =>
              handleDrawerNavigation("/amc/checklist/user/advanced")
            }
          >
            <ListItemIcon>
              <ChecklistIcon />
            </ListItemIcon>
            <ListItemText primary="User Checklist (Advanced)" />
          </ListItemButton>
        </List>
           <Divider sx={{ my: 1, borderBottomWidth: 2  }} />
      </Collapse>
    </>
  );

  const renderMembershipLinks = () => (
  <>
    <ListItemButton onClick={() => handleSectionToggle("membership")}>
      <ListItemIcon>
        <RememberMeIcon />
      </ListItemIcon>
      <ListItemText primary="Membership Management" />
      {openSections.membership ? <ExpandLess /> : <ExpandMore />}
    </ListItemButton>

    <Collapse in={openSections.membership} timeout="auto" unmountOnExit>

      {/* 🔹 ADMIN ONLY LINKS */}
      {userRole === "admin" && (
        <>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/membership")}
          >
            <ListItemIcon>
              <DesktopWindowsIcon />
            </ListItemIcon>
            <ListItemText primary="Approve Membership" />
          </ListItemButton>

          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/membership/bulk-upload")}
          >
            <ListItemIcon>
              <CloudUploadIcon />
            </ListItemIcon>
            <ListItemText primary="Bulk Data Upload" />
          </ListItemButton>

          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/membership/reports")}
          >
            <ListItemIcon>
              <ReportIcon />
            </ListItemIcon>
            <ListItemText primary="Reports" />
          </ListItemButton>
        </>
      )}

      {/* 🔹 CUSTOMER + SHOP + ADMIN */}
      {["customer", "shop", "admin"].includes(userRole) && (
        <>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/membership-form")}
          >
            <ListItemIcon>
              <AssignmentIndIcon />
            </ListItemIcon>
            <ListItemText primary="Membership Form" />
          </ListItemButton>

          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/membership/card")}
          >
            <ListItemIcon>
              <CardMembershipIcon />
            </ListItemIcon>
            <ListItemText primary="Digital Membership Card" />
          </ListItemButton>
        </>
      )}
         <Divider sx={{ my: 1, borderBottomWidth: 2  }} />
    </Collapse>
  </>
  
);



  const renderContractManagementLinks = () => (
    <>
      <ListItemButton onClick={() => handleSectionToggle("contractManagement")}>
        <ListItemIcon>
          <ScheduleIcon />
        </ListItemIcon>
        <ListItemText primary="Contract Management" />
        {openSections.contractManagement ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse
        in={openSections.contractManagement}
        timeout="auto"
        unmountOnExit
      >
        <List component="div" disablePadding>
          <ListItemButton
            sx={{ pl: 4 }}
            onClick={() => handleDrawerNavigation("/contracts/list")}
          >
            <ListItemIcon>
              <EventNoteIcon />
            </ListItemIcon>
            <ListItemText primary="All Contracts" />
          </ListItemButton>
          {userRole === "admin" && (
            <ListItemButton
              sx={{ pl: 4 }}
              onClick={() => handleDrawerNavigation("/contracts/new")}
            >
              <ListItemIcon>
                <AddCircleOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="New Contract" />
            </ListItemButton>
          )}
        </List>
      </Collapse>
    </>
  );

  const drawerContent = (
    <Box
      sx={{
        width: 270,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      role="presentation"
    >
      <List sx={{ flexGrow: 1, p: 1 }}>
        <ListItemButton onClick={() => handleDrawerNavigation("/")}>
          <ListItemIcon>
            <HomeIcon/>
          </ListItemIcon>
          <ListItemText
            primary="Home"
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </ListItemButton>
        <Divider sx={{ my: 1 }} />

        {userData && (
          <>
            {isAmcRoute ? (
              renderAmcLinks()
            ) : (
              <>
                {userRole === "superadmin" && renderSuperAdminLinks()}
                {userRole === "admin" && renderAdminLinks()}
                {(["admin", "customer"].includes(userRole) || isFacilityOperatorRole) &&
                  renderWarrantyLinks()}
                {(["admin", "customer"].includes(userRole) || isTechnicianRole) && renderAmcLinks()}
                {["admin", "customer"].includes(userRole) &&
                  renderMembershipLinks()}

                {/* --- MODIFIED --- */}
                {["admin", "user"].includes(userRole) &&
                  renderContractManagementLinks()}
              </>
            )}
          </>
        )}
      </List>

      {userData && (
        <Box sx={{ p: 2 }}>
          <Divider />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 1.5,
              display: "block",
              wordBreak: "break-word",
            }}
          >
            Logged in as: {userData.email} <br /> Role: {getDisplayRole(userRole)}
          </Typography>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mt: 1.5,
              backgroundColor: (theme) => `${theme.palette.error.light}40`,
              "&:hover": {
                backgroundColor: (theme) => `${theme.palette.error.light}60`,
              },
              borderRadius: 1,
            }}
          >
            <ListItemIcon>
              <LogoutIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontWeight: 600, color: "error.main" }}
            />
          </ListItemButton>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'rgba(254, 252, 232, 0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(240, 208, 96, 0.25)',
          color: '#1c1400',
        }}
      >
        <Toolbar>
          {!isRegisterPage && (
            <IconButton sx={{ color: '#1c1400' }} onClick={toggleDrawer(true)} edge="start">
              <MenuIcon />
            </IconButton>
          )}

          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              cursor: "pointer",
              ml: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Unbounded', cursive",
                fontWeight: 900,
                color: '#1c1400',
                fontSize: '1.1rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: 1,
              }}
            >
              Aalay<span style={{ color: '#f59e0b', fontSize: '1.4rem' }}>.</span>
            </Typography>

            <Typography
              variant="caption"
              component="div"
              sx={{
                fontStyle: 'italic',
                color: '#1c1400',
                opacity: 0.85,
                fontSize: '0.7rem',
                mt: 0.2,
                fontFamily: 'inherit',
                textTransform: 'none',
              }}
            >
              Intelligent Operations by OmneVu.com
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box>
            {userData ? (
              <>
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <Avatar sx={{ bgcolor: '#f59e0b', color: '#1c1400', fontWeight: 700 }}>
                    {userData.displayName?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem disabled>{userData.email}</MenuItem>
                  <MenuItem disabled>Role: {getDisplayRole(userRole)}</MenuItem>
                  {userRole === "superadmin" && (
                    <MenuItem
                      onClick={() => {
                        navigate("/superadmin");
                        setAnchorEl(null);
                      }}
                    >
                      Tenant Management
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                onClick={() => navigate("/login")}
                sx={{
                  color: '#1c1400',
                  border: '1.5px solid #1c1400',
                  borderRadius: '60px',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    background: '#1c1400',
                    color: '#fefce8',
                  },
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Toolbar />

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        {drawerContent}
      </Drawer>
    </>
  );
}

export default MainNavbar;
